/**
 * useWebSocket — Low-level WebSocket hook with auto-reconnect.
 * Higher-level integration via useRealtimeInvalidation.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const RECONNECT_DELAY_MS = 3_000;
const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Resolve the WebSocket origin.
 *
 * Explicit `off` / `disabled` / empty `VITE_WS_BASE_URL` means "do not
 * open a socket" — Playwright's Vite-only e2e server has no backend, so
 * the historical `ws://localhost:8000` default produces handshake
 * `ERR_CONNECTION_REFUSED` noise (not XSS, not a live channel).
 * Unset env still defaults to local backend for ordinary `vite dev`.
 */
export function resolveWebSocketBaseUrl(
  envValue: string | undefined = import.meta.env.VITE_WS_BASE_URL as
    | string
    | undefined,
): string | null {
  if (envValue === '' || envValue === 'off' || envValue === 'disabled') {
    return null;
  }
  return envValue ?? 'ws://localhost:8000';
}

export const useWebSocket = (channel?: string) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<unknown>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<number | null>(null);
  const disposedRef = useRef(false);

  const connect = useCallback(() => {
    if (disposedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const baseUrl = resolveWebSocketBaseUrl();
    if (!baseUrl) return;

    const wsUrl = channel
      ? `${baseUrl}/ws?channel=${channel}`
      : `${baseUrl}/ws`;

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setIsConnected(true);
      reconnectAttempts.current = 0;
      if (channel) {
        ws.send(JSON.stringify({ action: 'subscribe', channel }));
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLastMessage(data);
      } catch {
        // Ignore non-JSON messages
      }
    };

    ws.onerror = () => {
      // Error handling — reconnect on close will cover this
    };

    ws.onclose = () => {
      setIsConnected(false);
      wsRef.current = null;

      // Guard: don't reconnect after unmount / disposal
      if (disposedRef.current) return;

      // Auto-reconnect with backoff
      if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts.current++;
        reconnectTimer.current = window.setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };

    wsRef.current = ws;
  }, [channel]);

  useEffect(() => {
    disposedRef.current = false;
    connect();

    return () => {
      disposedRef.current = true;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (wsRef.current) {
        if (channel && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: 'unsubscribe', channel }));
        }
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [channel, connect]);

  return { isConnected, lastMessage };
};
